<template>
  <div>
    <b-row class="mb-1">
      <b-col>
        <h1 class="font-weight-bolder mb-0">
          Términos y privacidad
        </h1>
        <small class="text-muted">
          Al publicar una versión nueva, cada cliente tiene que aceptarla la siguiente vez que entre a la
          plataforma. Una versión publicada ya no se puede editar.
        </small>
      </b-col>
    </b-row>

    <b-overlay
      :show="loading"
      rounded="sm"
    >
      <b-row>
        <b-col
          v-for="section in sections"
          :key="section.kind"
          lg="6"
          class="mb-2"
        >
          <b-card
            no-body
            class="h-100 mb-0"
          >
            <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
              <h4 class="mb-0">
                {{ section.label }}
              </h4>
              <b-link
                v-if="section.current"
                :to="{ name: section.route }"
                target="_blank"
              >
                Ver publicada
                <feather-icon
                  icon="ExternalLinkIcon"
                  size="13"
                />
              </b-link>
            </b-card-header>

            <b-card-body>
              <div
                v-if="section.current"
                class="legal-status legal-status--published"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="18"
                />
                <div>
                  <strong>Versión {{ section.current.version }} vigente</strong>
                  <div class="text-muted small">
                    Desde el {{ formatDate(section.current.published_at) }} ·
                    {{ section.current.acceptances_count }} {{ section.current.acceptances_count === 1 ? 'aceptación' : 'aceptaciones' }}
                  </div>
                </div>
              </div>
              <div
                v-else
                class="legal-status legal-status--missing"
              >
                <feather-icon
                  icon="AlertCircleIcon"
                  size="18"
                />
                <div>
                  <strong>Sin versión publicada</strong>
                  <div class="text-muted small">
                    Mientras no se publique, a nadie se le pide aceptarlo.
                  </div>
                </div>
              </div>

              <div
                v-if="section.draft"
                class="legal-draft"
              >
                <div>
                  <strong>Borrador de la versión {{ section.draft.version }}</strong>
                  <div class="text-muted small">
                    Última edición: {{ formatDate(section.draft.updated_at) }}
                  </div>
                </div>
                <div class="legal-draft__actions">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="openEditor(section.kind, section.draft)"
                  >
                    Editar
                  </b-button>
                  <b-button
                    variant="primary"
                    size="sm"
                    :disabled="busy"
                    @click="publish(section, section.draft)"
                  >
                    Publicar
                  </b-button>
                  <b-button
                    variant="flat-danger"
                    size="sm"
                    :disabled="busy"
                    @click="removeDraft(section.draft)"
                  >
                    Eliminar
                  </b-button>
                </div>
              </div>
              <b-button
                v-else
                variant="outline-primary"
                class="mt-1"
                @click="openEditor(section.kind)"
              >
                <feather-icon
                  icon="PlusIcon"
                  class="mr-50"
                />
                {{ section.current ? 'Preparar versión nueva' : 'Cargar el documento' }}
              </b-button>

              <div
                v-if="section.history.length"
                class="legal-history"
              >
                <h6 class="text-muted text-uppercase small mb-50">
                  Versiones anteriores
                </h6>
                <div
                  v-for="item in section.history"
                  :key="item.id"
                  class="legal-history__row"
                >
                  <span>Versión {{ item.version }}</span>
                  <span class="text-muted">{{ formatDate(item.published_at) }}</span>
                  <span class="text-muted">{{ item.acceptances_count }} aceptaciones</span>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>

    <b-modal
      v-model="editorOpen"
      size="xl"
      centered
      scrollable
      no-close-on-backdrop
      :title="editorTitle"
    >
      <b-alert
        :show="Boolean(errorMessage)"
        variant="danger"
      >
        <div class="alert-body">
          {{ errorMessage }}
        </div>
      </b-alert>
      <b-row>
        <b-col lg="6">
          <b-form-group
            label="Título"
            label-for="legal-title"
          >
            <b-form-input
              id="legal-title"
              v-model="draft.title"
              maxlength="120"
            />
          </b-form-group>
          <b-form-group
            label="Texto"
            label-for="legal-body"
          >
            <b-form-textarea
              id="legal-body"
              v-model="draft.body"
              rows="22"
              class="legal-editor"
              placeholder="## 1. Quiénes somos&#10;Texto del párrafo.&#10;&#10;- Primera viñeta&#10;- Segunda viñeta"
            />
          </b-form-group>
          <div class="legal-format-help">
            <strong>Formato:</strong>
            <code>## Título de sección</code>
            <code>- viñeta</code>
            <code>1. lista numerada</code>
            <code>**negritas**</code>
            y una línea en blanco para separar párrafos.
          </div>
        </b-col>
        <b-col lg="6">
          <div class="legal-preview">
            <div class="legal-preview__label">
              Así se verá
            </div>
            <h2 class="legal-preview__title">
              {{ draft.title }}
            </h2>
            <legal-text :body="draft.body" />
          </div>
        </b-col>
      </b-row>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="saving"
          @click="editorOpen = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving"
          @click="saveDraft"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
          />
          Guardar borrador
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardHeader, BCardBody, BButton, BLink, BOverlay, BModal, BAlert,
  BFormGroup, BFormInput, BFormTextarea, BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import LegalText from '@/components/legal/LegalText.vue'

const KINDS = [
  {
    kind: 'terms', label: 'Términos y Condiciones', title: 'Términos y Condiciones de Uso', route: 'legal-terms',
  },
  {
    kind: 'privacy', label: 'Aviso de Privacidad', title: 'Aviso de Privacidad Integral', route: 'legal-privacy',
  },
]

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BLink,
    BOverlay,
    BModal,
    BAlert,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BSpinner,
    LegalText,
  },

  data() {
    return {
      loading: false,
      busy: false,
      saving: false,
      editorOpen: false,
      errorMessage: '',
      draft: {
        id: null, kind: 'terms', title: '', body: '',
      },
    }
  },

  computed: {
    ...mapGetters('legal', { documents: 'adminDocuments' }),

    sections() {
      return KINDS.map(item => {
        const versions = this.documents.filter(document => document.kind === item.kind)
        const published = versions.filter(document => document.status === 'published')
        return {
          ...item,
          current: published[0] || null,
          draft: versions.find(document => document.status === 'draft') || null,
          history: published.slice(1),
        }
      })
    },

    editorTitle() {
      const section = KINDS.find(item => item.kind === this.draft.kind)
      return `${this.draft.id ? 'Editar borrador' : 'Nueva versión'} · ${section ? section.label : ''}`
    },
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('legal', ['fetchAdminLegalDocuments', 'saveLegalDocument', 'publishLegalDocument', 'deleteLegalDocument']),

    formatDate(value) {
      if (!value) return ''
      return new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(value))
    },

    notify(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon', variant },
      })
    },

    errorFrom(error, fallback) {
      const messages = error?.response?.data?.messages
      return Array.isArray(messages) && messages.length ? messages.join('. ') : fallback
    },

    async load() {
      this.loading = true
      try {
        await this.fetchAdminLegalDocuments()
      } finally {
        this.loading = false
      }
    },

    openEditor(kind, draft = null) {
      const section = this.sections.find(item => item.kind === kind)
      const base = draft || section.current
      this.errorMessage = ''
      this.draft = {
        id: draft ? draft.id : null,
        kind,
        title: base ? base.title : section.title,
        body: base ? base.body : '',
      }
      this.editorOpen = true
    },

    async saveDraft() {
      if (!this.draft.title.trim() || !this.draft.body.trim()) {
        this.errorMessage = 'El documento necesita título y texto'
        return
      }
      this.saving = true
      this.errorMessage = ''
      try {
        await this.saveLegalDocument({ ...this.draft })
        this.editorOpen = false
        this.notify('Borrador guardado')
      } catch (error) {
        this.errorMessage = this.errorFrom(error, 'No se pudo guardar el borrador')
      } finally {
        this.saving = false
      }
    },

    async publish(section, draft) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Todos los clientes tendrán que aceptar la versión ${draft.version} de ${section.label} la próxima vez que entren. Una vez publicada ya no se puede editar.`,
        {
          title: `¿Publicar la versión ${draft.version}?`,
          okTitle: 'Publicar',
          cancelTitle: 'Cancelar',
          okVariant: 'primary',
          centered: true,
        },
      )
      if (!confirmed) return
      this.busy = true
      try {
        await this.publishLegalDocument(draft.id)
        this.notify(`Versión ${draft.version} publicada`)
      } catch (error) {
        this.notify(this.errorFrom(error, 'No se pudo publicar'), 'danger')
      } finally {
        this.busy = false
      }
    },

    async removeDraft(draft) {
      const confirmed = await this.$bvModal.msgBoxConfirm('El borrador se elimina y no se puede recuperar.', {
        title: '¿Eliminar el borrador?',
        okTitle: 'Eliminar',
        cancelTitle: 'Cancelar',
        okVariant: 'danger',
        centered: true,
      })
      if (!confirmed) return
      this.busy = true
      try {
        await this.deleteLegalDocument(draft.id)
        this.notify('Borrador eliminado')
      } catch (error) {
        this.notify(this.errorFrom(error, 'No se pudo eliminar el borrador'), 'danger')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.legal-status {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
}

.legal-status--published {
  background: rgba(40, 199, 111, 0.1);
  color: #1f7a45;
}

.legal-status--missing {
  background: rgba(255, 159, 67, 0.12);
  color: #a15c00;
}

.legal-draft {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px dashed #c4c9d4;
  border-radius: 8px;
}

.legal-draft__actions {
  display: flex;
  gap: 0.5rem;
}

.legal-history {
  margin-top: 1.5rem;
}

.legal-history__row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1rem;
  padding: 0.4rem 0;
  border-top: 1px solid #ebe9f1;
  font-size: 0.9rem;
}

.legal-editor {
  font-family: Menlo, Consolas, monospace;
  font-size: 0.85rem;
  line-height: 1.5;
}

.legal-format-help {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: #6e6b7b;
}

.legal-preview {
  height: 100%;
  max-height: 640px;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fff;
}

.legal-preview__label {
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6e6b7b;
}

.legal-preview__title {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 700;
}
</style>
